'use strict';

/**
 * Authentication encryption and security utilities
 * Handles secure storage, key derivation, and data validation
 */
window.SoracomAuthEncryption = (() => {

  // Encryption constants
  const PBKDF_ITERATIONS = 300000; // Increased iterations for stronger key derivation
  const MAX_STORAGE_AGE = 60 * 60 * 1000; // 1 hour in ms
  const MAX_API_KEY_LENGTH = 500;
  const MAX_TOKEN_LENGTH = 8192;

  /**
   * Derive encryption key from material using PBKDF2.
   * @param {string} material - Source material (usually operatorId).
   * @param {Uint8Array} salt - A random salt for the key derivation.
   * @returns {Promise<CryptoKey>} Derived encryption key.
   */
  async function deriveKey(material, salt) {
    const enc = new TextEncoder();
    const base = await crypto.subtle.importKey(
      'raw', enc.encode(String(material)), { name: 'PBKDF2' }, false, ['deriveKey']
    );
    return crypto.subtle.deriveKey(
      { name: 'PBKDF2', salt, iterations: PBKDF_ITERATIONS, hash: 'SHA-256' },
      base,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Encrypt data using AES-GCM with a randomly generated salt.
   * @param {string} data - Data to encrypt.
   * @param {string} keySeed - Key derivation seed.
   * @returns {Promise<Object>} Encrypted data object.
   * @throws {Error} If Web Crypto API is not available or encryption fails.
   */
  async function encryptData(data, keySeed) {
    if (!crypto.subtle) {
      throw new Error('Web Crypto API not available. Cannot encrypt data.');
    }

    try {
      const salt = crypto.getRandomValues(new Uint8Array(16));
      const key = await deriveKey(keySeed, salt);
      const iv = crypto.getRandomValues(new Uint8Array(12));
      const encodedData = new TextEncoder().encode(data);

      const encrypted = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        key,
        encodedData
      );

      return {
        encrypted: true,
        data: Array.from(new Uint8Array(encrypted)),
        iv: Array.from(iv),
        salt: Array.from(salt),
        keySeed: keySeed,
        timestamp: Date.now()
      };
    } catch (e) {
      console.error('Encryption failed:', window.SoracomAuthUtils?.sanitizeForLogging?.(e?.message || e) || '[error details unavailable]');
      throw new Error('Encryption failed.');
    }
  }

  /**
   * Decrypt data using AES-GCM.
   * @param {Object} encryptedObj - Encrypted data object.
   * @returns {Promise<string|null>} Decrypted data or null if decryption fails.
   */
  async function decryptData(encryptedObj) {
    if (!encryptedObj || !encryptedObj.encrypted) {
      return null; // Do not process unencrypted or invalid data.
    }

    if (!crypto.subtle) {
      console.error('Web Crypto API not available but data is encrypted');
      return null;
    }

    try {
      if (!encryptedObj.data || !encryptedObj.iv || !encryptedObj.salt || encryptedObj.keySeed === undefined) {
        console.error('Encrypted object is missing required fields for decryption.');
        return null;
      }

      const keySeed = encryptedObj.keySeed;
      const salt = new Uint8Array(encryptedObj.salt);
      const key = await deriveKey(keySeed, salt);
      const iv = new Uint8Array(encryptedObj.iv);
      const encryptedData = new Uint8Array(encryptedObj.data);

      const decrypted = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv },
        key,
        encryptedData
      );

      return new TextDecoder().decode(decrypted);
    } catch (e) {
      console.error('Decryption failed:', window.SoracomAuthUtils?.sanitizeForLogging?.(e?.message || e) || '[error details unavailable]');
      return null;
    }
  }

  /**
   * Validate authentication data structure and content.
   * @param {Object} auth - Authentication data to validate.
   * @returns {boolean} True if valid.
   */
  function validateAuthData(auth) {
    if (!auth || typeof auth !== 'object') {
      return false;
    }

    if (!auth.apiKey || typeof auth.apiKey !== 'string' || auth.apiKey.trim() === '') {
      return false;
    }

    if (!auth.token || typeof auth.token !== 'string' || auth.token.trim() === '') {
      return false;
    }

    if (auth.apiKey.length > MAX_API_KEY_LENGTH) {
      return false;
    }

    if (auth.token.length > MAX_TOKEN_LENGTH) {
      return false;
    }

    if (auth.timestamp !== undefined) {
      if (typeof auth.timestamp !== 'number' ||
        auth.timestamp < 0 ||
        auth.timestamp > Date.now() + 60000) {
        return false;
      }

      const age = Date.now() - auth.timestamp;
      if (age > MAX_STORAGE_AGE) {
        return false;
      }
    }

    if (auth.operatorId !== undefined &&
      (typeof auth.operatorId !== 'string' || auth.operatorId.length > 100)) {
      return false;
    }

    return true;
  }

  // Public API
  return {
    encryptData,
    decryptData,
    validateAuthData
  };

})();
